import {
  IMatchProfile,
  IMatchProfileCandidates,
  IMatchProfileCandidatesBody,
  IMatchProfileCandidatesParams
} from 'core/interface/matchProfile';
import { IresponseDTO } from 'core/interface/responseDTO';
import { store } from '../../redux/store';
import ApiBase from 'services/base/apiBase';
import {
  SaveCultureProfile,
  SaveMatchProfile,
  SaveMatchProfileCandidate,
  SaveMatchProfileOne,
  SaveOverview,
  SaveOverviewOne,
  SaveSearch
} from '../../redux/slices/match';
import { prepareUpdatedData } from 'helpers/stateChanges';

export class MatchProfileService {
  public static apiBase = new ApiBase<FormData | IresponseDTO<IMatchProfile>>();
  private static activeMatchProfileId: number | null = null;
  private static activeMatchProfileCandidatesId: string | null = null;

  public static async getMatchProfiles(
    query?: boolean,
    limit?: number,
    offset?: number
  ): Promise<IresponseDTO<IMatchProfile[]> | void> {
    const { dispatch } = store;
    try {
      let url = `/match/profiles/?culture=${query}`;
      const params = [];
      if (limit !== undefined) params.push(`limit=${limit}`);
      if (offset !== undefined) params.push(`offset=${offset}`);
      if (params.length) {
        url += `&${params.join('&')}`;
      }
      const res = await this.apiBase.getAsync(url);
      if (query) {
        dispatch(SaveCultureProfile(res));
      } else {
        dispatch(SaveMatchProfile(res));
      }
      return res;
    } catch (error) {
      console.error('Error fetching:', error);
    }
  }

  public static async getMatchProfilesSearch(
    query: any
  ): Promise<IresponseDTO<IMatchProfile[]> | void> {
    const { dispatch } = store;
    try {
      const res = await this.apiBase.getAsync(
        `/match/profiles/search/?category=culture&q=${query}`
      );
      dispatch(SaveSearch(res));
      return res;
    } catch (error) {
      console.error('Error fetching:', error);
    }
  }

  public static async getMatchProfileById(
    id: number
  ): Promise<IMatchProfile | void> {
    const { dispatch } = store;
    this.activeMatchProfileId = id;
    try {
      const res = await this.apiBase.getAsync(`/match/profiles/${id}/`);
      if (this.activeMatchProfileId === id) {
        dispatch(SaveMatchProfileOne(res));
      }
      return res;
    } catch (error) {
      console.error('Error fetching:', error);
    }
  }

  public static async getCandidatesOverview(
    id?: number,
    limit?: number,
    offset?: number,
    searchQuery?: string,
    ordering?: string
  ): Promise<IresponseDTO<IMatchProfile[]> | void> {
    const { dispatch } = store;
    try {
      let url = `/match/selected_candidates/?profileID=${id}`;
      const params = [];
      if (limit !== undefined) params.push(`limit=${limit}`);
      if (offset !== undefined) params.push(`offset=${offset}`);
      if (searchQuery) params.push(`query=${encodeURIComponent(searchQuery)}`);
      if (ordering) params.push(`ordering=${ordering}`);
      if (params.length) {
        url += `&${params.join('&')}`;
      }
      const res = await this.apiBase.getAsync(url);
      if (id) {
        dispatch(SaveOverviewOne(res));
      } else {
        dispatch(SaveOverview(res));
      }
      return res;
    } catch (error) {
      console.error('Error fetching:', error);
    }
  }

  public static async updateSelectedCandidate(
    id: number,
    data: { stage: number }
  ): Promise<any | null> {
    const { dispatch, getState } = store;
    try {
      const res = await this.apiBase.updateAsync(
        `/match/selected_candidates/${id}/`,
        data
      );
      const matchProfileOne = getState().match.matchProfileOne;
      if (matchProfileOne?.selected_candidates) {
        dispatch(
          SaveMatchProfileOne({
            ...matchProfileOne,
            selected_candidates: matchProfileOne.selected_candidates.map(
              (el: { id: number }) => (el.id === id ? { ...el, ...res } : el)
            )
          })
        );
      }
      const overviewState = prepareUpdatedData(
        'update',
        'match',
        res,
        'matchOverViewOne'
      );
      dispatch(SaveOverviewOne(overviewState));
      return res;
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  public static async deleteCultureProfile(
    id: number
  ): Promise<boolean | null> {
    const { dispatch, getState } = store;
    try {
      const res = await this.apiBase.deleteAsync(
        `/match/selected_candidates/${id}/`
      );
      const matchProfileOne = getState().match.matchProfileOne;
      if (matchProfileOne?.selected_candidates) {
        dispatch(
          SaveMatchProfileOne({
            ...matchProfileOne,
            selected_candidates: matchProfileOne.selected_candidates.filter(
              (el: { id: number }) => el.id !== id
            )
          })
        );
      }
      const updatedState = prepareUpdatedData(
        'delete',
        'match',
        {
          id
        },
        'matchOverViewOne'
      );
      dispatch(SaveOverviewOne(updatedState));
      return res;
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  public static async selectCandidate(
    profileId: number,
    candidateId: number
  ): Promise<any> {
    return this.apiBase.postAsync(`/match/selected_candidates/`, {
      profile: profileId,
      candidate: candidateId
    });
  }

  public static async getMatchProfileCandidatesById(
    id: string,
    params: IMatchProfileCandidatesParams,
    body: IMatchProfileCandidatesBody | Record<string, unknown> = {}
  ): Promise<IMatchProfileCandidates | void> {
    try {
      const { dispatch } = store;
      this.activeMatchProfileCandidatesId = id;

      const res = await this.apiBase.postAsync(
        `/match/profiles/${id}/find_candidates/`,
        body ?? {},
        params
      );

      if (this.activeMatchProfileCandidatesId === id) {
        dispatch(SaveMatchProfileCandidate(res));
      }

      return res;
    } catch (error) {
      console.error('Error fetching:', error);
    }
  }

  public static async removeMatchProfileCandidatesById(
    id: string
  ): Promise<IMatchProfileCandidates> {
    return this.apiBase.postAsync(`/match/profiles/${id}/remove_candidate/`);
  }

  public static async deleteMatchProfile(
    id: number,
    isCulture: boolean
  ): Promise<boolean | null> {
    const { dispatch } = store;
    try {
      const res = await this.apiBase.deleteAsync(`/match/profiles/${id}/`);

      if (isCulture) {
        const updatedState = prepareUpdatedData(
          'delete',
          'match',
          {
            id
          },
          'cultureProfile'
        );
        dispatch(SaveCultureProfile(updatedState));
      } else {
        const updatedState = prepareUpdatedData(
          'delete',
          'match',
          {
            id
          },
          'matchProfile'
        );
        dispatch(SaveMatchProfile(updatedState));
      }

      return res;
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  public static async createMatchProfile(
    query: boolean,
    body: any
  ): Promise<any> {
    const { dispatch } = store;
    try {
      const res = await this.apiBase.postAsync(
        `/match/profiles/?culture=${query}`,
        body
      );
      if (body?.is_culture) {
        const updatedState = prepareUpdatedData(
          'create',
          'match',
          res,
          'cultureProfile'
        );
        dispatch(SaveCultureProfile(updatedState));
      }
      return res;
    } catch (error) {
      console.error('Error fetching:', error);
    }
  }

  public static async editMatchProfile(
    id: number,
    query: boolean,
    body: any
  ): Promise<any> {
    const { dispatch } = store;
    try {
      const res = await this.apiBase.updateAsync(`/match/profiles/${id}/`, body);
      const updatedState = prepareUpdatedData(
        'update',
        'match',
        res,
        'matchProfile'
      );
      dispatch(SaveMatchProfile(updatedState));
      return res;
    } catch (error) {
      console.error('Error fetching:', error);
    }
  }
}
